// Derives running position from the phase timeline for a trade.
// Phase → fill mapping:
//   "Open - Entry" or "Open - Added"  → BUY (long) / SELL (short)  ← adds size
//   "Open - Trimmed" or "Closed"      → SELL (long) / BUY (short) ← reduces size
//     ("Closed" is treated as a full-position sell — the dialog auto-fills
//      shares to whatever is currently on hand.)
// Only phases with a numeric `shares` and `price` contribute to fills.

export const BUY_PHASES = new Set(["Open - Entry", "Open - Added"]);
export const SELL_PHASES = new Set(["Open - Trimmed", "Closed"]);
export const POSITION_PHASES = new Set([...BUY_PHASES, ...SELL_PHASES]);

// Deterministic ordering — chronological.
function sortByCreated(phases) {
  return [...phases].sort((a, b) => {
    const av = new Date(a.created_at || 0).getTime();
    const bv = new Date(b.created_at || 0).getTime();
    return av - bv;
  });
}

/**
 * Convert phases with shares fills to broker-fill rows suitable for the
 * CloseTradeDialog. Rows are chronologically ordered.
 * `direction` = "long" | "short" — flips BUY/SELL semantics for short trades.
 */
export function phasesToFills(phases, direction = "long") {
  const isLong = direction !== "short";
  const rows = [];
  for (const p of sortByCreated(phases || [])) {
    const shares = Number(p.shares);
    const price = Number(p.price);
    if (!shares || !price || shares <= 0 || price <= 0) continue;
    let side;
    if (BUY_PHASES.has(p.phase)) side = isLong ? "BUY" : "SELL";
    else if (SELL_PHASES.has(p.phase)) side = isLong ? "SELL" : "BUY";
    else continue;
    rows.push({ side, shares, price, at: p.created_at, phase: p.phase });
  }
  return rows;
}

/**
 * Running position & FIFO cost basis derived from phase fills.
 * Returns { position, avgCost, lots }.
 *   - position: signed shares held (positive = long, negative = short)
 *   - avgCost: weighted average cost basis of currently held shares
 *   - lots: FIFO queue snapshot (for advanced UI if needed)
 * If `upToDate` is provided, only phases created strictly before that ISO
 * timestamp are counted — useful when composing a NEW phase and we want the
 * running snapshot as of that moment.
 */
export function positionFromPhases(phases, direction = "long", upToDate = null) {
  const rows = phasesToFills(phases, direction);
  const cutoff = upToDate ? new Date(upToDate).getTime() : null;
  let lots = [];
  let lotSide = direction === "short" ? "short" : "long";
  for (const r of rows) {
    if (cutoff != null) {
      const t = new Date(r.at || 0).getTime();
      if (!(t < cutoff)) continue;
    }
    if (lots.length === 0) {
      lotSide = r.side === "BUY" ? "long" : "short";
      lots.push({ shares: r.shares, price: r.price });
      continue;
    }
    const opening = (lotSide === "long" && r.side === "BUY") || (lotSide === "short" && r.side === "SELL");
    if (opening) {
      lots.push({ shares: r.shares, price: r.price });
      continue;
    }
    let remaining = r.shares;
    while (remaining > 1e-9 && lots.length > 0) {
      const lot = lots[0];
      const consume = Math.min(lot.shares, remaining);
      lot.shares -= consume;
      remaining -= consume;
      if (lot.shares < 1e-9) lots.shift();
    }
    if (remaining > 1e-9) {
      lotSide = r.side === "BUY" ? "long" : "short";
      lots = [{ shares: remaining, price: r.price }];
    }
  }
  const totalShares = lots.reduce((a, l) => a + l.shares, 0);
  const totalCost = lots.reduce((a, l) => a + l.shares * l.price, 0);
  const avgCost = totalShares > 0 ? totalCost / totalShares : 0;
  const position = lotSide === "long" ? totalShares : -totalShares;
  return { position, avgCost, lots };
}

/**
 * % of position this phase represents, relative to the largest open size
 * the trade has reached across all fills — mirrors the backend
 * `_compute_phase_position_pct` used by the notification templates.
 * Returns null when the phase does not affect size (Idea/Management/etc.)
 * or shares/price are missing/invalid.
 */
export function phasePositionPct(allPhases, direction, thisPhase) {
  if (!thisPhase || !POSITION_PHASES.has(thisPhase.phase)) return null;
  const shares = Number(thisPhase.shares);
  const price = Number(thisPhase.price);
  if (!shares || shares <= 0 || !price || price <= 0) return null;
  const isLong = direction !== "short";
  const openingSide = isLong ? "BUY" : "SELL";
  const fills = phasesToFills(allPhases, direction);
  let runningOpen = 0;
  let peakOpen = 0;
  for (const f of fills) {
    if (f.side === openingSide) runningOpen += f.shares;
    else runningOpen = Math.max(runningOpen - f.shares, 0);
    if (runningOpen > peakOpen) peakOpen = runningOpen;
  }
  if (peakOpen <= 0) return null;
  return Math.round((shares / peakOpen) * 1000) / 10;
}
